import { DisplaySchema, DisplayType, nameof, ObjectSchema, ViewConfig, ViewField, ViewType } from '@summit/models';

import { CredentialParameters, CredentialParametersMapping } from './credential-parameters.enum';
import { IntegrationConfiguration } from './integration-configuration';
import { ServiceMapping } from './service-provider.enum';
import { TenantCredential } from './tenant-credential';

export class TenantCredentialSchema extends DisplaySchema<TenantCredential> {
  readonly refTitle = 'Service Credentials';
  override readonly refServerAPI = 'TenantCredentials';
  override readonly refServerObjectName = 'TenantCredential';
  override readonly clientOnlyFields = ['synchronize', 'tenant', 'hub', 'company'];
  readonly refColumnList: ObjectSchema<TenantCredential> = {
    id: { propertyName: 'id', formField: true, displayType: DisplayType.Hidden },
    name: {
      propertyName: 'name',
      formField: true,
      columnField: true,
      displayName: 'Name',
      validationMessages: [{ type: 'required', message: 'Name is required' }]
    },
    tenantID: {
      propertyName: 'tenantID',
      formField: true,
      displayName: 'Tenant'
    },
    tenant: {
      propertyName: 'tenant',
      columnField: true,
      displayName: 'Tenant',
      displayType: DisplayType.ShortString
    },
    service: {
      propertyName: 'service',
      formField: true,
      columnField: true,
      displayName: 'Service',
      validationMessages: [{ type: 'required', message: 'Service is required' }],
      displayType: DisplayType.Enum,
      displayTypeParameter: ServiceMapping
    },
    hubID: {
      propertyName: 'hubID',
      formField: true,
      displayName: 'Primary Hub'
    },
    hub: {
      propertyName: 'hub',
      columnField: true,
      displayName: 'Primary Hub',
      displayType: DisplayType.ShortString
    },
    includePnD: {
      propertyName: 'includePnD',
      formField: true,
      columnField: true,
      displayName: 'Include P&D Settlements',
      displayType: DisplayType.Boolean,
      displayHint: 'Include True P&D, not P&D Spots'
    },
    excludeLinehaul: {
      propertyName: 'excludeLinehaul',
      formField: true,
      columnField: true,
      displayName: 'Exclude Linehaul',
      displayType: DisplayType.Boolean
    },
    supportingHubs: {
      propertyName: 'supportingHubs',
      columnField: true,
      displayName: 'Supporting Hubs',
      displayClass: 'preserve-format'
    },
    companyID: {
      propertyName: 'companyID',
      formField: true,
      displayName: 'Primary Company'
    },
    company: {
      propertyName: 'company',
      columnField: true,
      displayName: 'Primary Company',
      displayType: DisplayType.Name
    },

    enabled: {
      propertyName: 'enabled',
      formField: true,
      columnField: true,
      displayName: 'Enabled',
      displayType: DisplayType.Boolean
    },
    archived: {
      propertyName: 'archived',
      formField: true,
      columnField: true,
      displayName: 'Archived',
      displayType: DisplayType.Boolean
    },
    startDate: {
      propertyName: nameof<TenantCredential>('startDate'),
      formField: true,
      columnField: true,
      displayName: 'Start Date',
      displayType: DisplayType.DateUtc,
      displayHint: 'Leave blank except for Company Acquisitions'
    },
    synchronize: {
      propertyName: 'synchronize',
      formField: true,
      displayName: CredentialParametersMapping[CredentialParameters.Synchronize],
      displayType: DisplayType.Boolean
    },
    autoImport: {
      propertyName: 'autoImport',
      formField: true,
      displayName: CredentialParametersMapping[CredentialParameters.AutoImport],
      displayType: DisplayType.Boolean
    },
    allowDelete: {
      propertyName: 'allowDelete',
      formField: true,
      displayName: CredentialParametersMapping[CredentialParameters.AllowDelete],
      displayType: DisplayType.Boolean
    },
    comment: {
      propertyName: 'comment',
      formField: true,
      columnField: true,
      displayName: 'Comment'
    },
    integrationConfig: {
      propertyName: nameof<TenantCredential>('integrationConfig'),
      columnField: true,
      displayName: 'Integration Config',
      children: {
        refNumber: {
          propertyName: nameof<IntegrationConfiguration>('refNumber'),
          formField: true,
          displayName: 'Integration Ref Number',
          displayType: DisplayType.Number
        },
        lastLocationPacketId: {
          propertyName: nameof<IntegrationConfiguration>('lastLocationPacketId'),
          formField: true,
          displayName: 'Last Location Packet ID',
          displayType: DisplayType.Number
        },
        lastHosPacketId: {
          propertyName: nameof<IntegrationConfiguration>('lastHosPacketId'),
          formField: true,
          displayName: 'Last HOS Packet ID',
          displayType: DisplayType.Number
        },
        lastHosRead: {
          propertyName: nameof<IntegrationConfiguration>('lastHosRead'),
          formField: true,
          displayName: 'Last HOS Read',
          displayType: DisplayType.DateTimeUtc
        },
        lastLocationRead: {
          propertyName: nameof<IntegrationConfiguration>('lastLocationRead'),
          formField: true,
          displayName: 'Last Location Read',
          displayType: DisplayType.DateTimeUtc
        }
      }
    },
    // Service Key Entry
    serviceUserName: {
      propertyName: 'serviceUserName',
      formField: true,
      displayName: 'User Name'
    },
    servicePassword: {
      propertyName: 'servicePassword',
      formField: true,
      displayName: 'Password'
    },
    serviceCode: {
      propertyName: 'serviceCode',
      formField: true,
      displayName: 'OAuth Code'
    },
    serviceGroup: {
      propertyName: 'serviceGroup',
      formField: true,
      displayName: 'Group'
    },
    serviceHost: {
      propertyName: 'serviceHost',
      formField: true,
      displayName: 'Host'
    },
    oAuthUrl: {
      propertyName: 'oAuthUrl',
      formField: true,
      displayType: DisplayType.Hidden
    }
  };

  readonly refViewModels: ViewConfig<TenantCredential>[] = [
    {
      id: '_tenant',
      name: 'Service Credentials',
      type: ViewType.Standard,
      viewColumns: ['name', 'enabled', 'comment'],
      columnOrder: ['name', 'enabled', 'comment'],
      sortParameters: { id: 'name', start: 'asc' },
      groupBy: ['service']
    }
  ];

  NewObject(jsonData?: Partial<TenantCredential>): TenantCredential {
    return new TenantCredential(jsonData);
  }

  public override getTdClassString(
    element: TenantCredential,
    _attribute: ViewField<TenantCredential>,
    _overFlow?: boolean
  ): string {
    const response = Array<string>();
    if (element?.archived) {
      response.push('alert alert-dark');
    }
    return response.join(' ');
  }
}
